import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Mail, Trash2, AlertCircle, Clock } from 'lucide-react';
import { submitDataDeletionRequest } from '../../services/api';
import styles from './Legal.module.scss';

const CONTACT_EMAIL = 'admin@wearefoth.com';
const APP_URL = 'https://www.wearefoth.com';

const DataDeletion = () => {
  const { t } = useTranslation('legal');
  const [form, setForm] = useState({
    email: '',
    name: '',
    reason: '',
    type: 'full',
    website: '',
    started_at: Date.now() / 1000,
  });
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      setError(t('deletion.form.error_required', 'Please provide your name and email address.'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await submitDataDeletionRequest(form);
      setReference(response.data.reference);
      setSubmitted(true);
    } catch (submitError) {
      const message = submitError.response?.data?.detail
        || submitError.response?.data?.email?.[0]
        || submitError.response?.data?.name?.[0]
        || t('deletion.form.error_failed', 'Could not submit deletion request. Please contact privacy@wearefoth.com.');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.legalPage}>
      <div className={styles.legalHero} style={{ background: 'linear-gradient(180deg, #18183A 0%, #10152F 100%)' }}>
        <div className={styles.container}>
          <div className={styles.heroBadge}>{t('common.badge', 'Data & Privacy')}</div>
          <h1 className={styles.heroTitle} style={{ color: '#fff' }}>{t('deletion.title', 'Data Deletion')}</h1>
          <p className={styles.heroSub} style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('deletion.desc', 'Request the deletion of your personal data from the FOTH platform.')}
          </p>
          <p className={styles.heroDesc} style={{ color: 'rgba(255,255,255,0.55)' }}>
            {t('deletion.desc', 'In compliance with GDPR, CCPA, and Meta\'s Platform Terms, FOTH allows any user to request deletion of their personal data.')}
          </p>
        </div>
      </div>

      <div className={styles.deletionLayout}>
        <div className={styles.container}>

          {/* What gets deleted */}
          <section className={styles.deletionInfoGrid}>
            <div className={styles.deletionInfoCard}>
              <Trash2 size={22} className={styles.deletionIcon} />
              <h3>{t('deletion.what_gets_deleted.title', 'What Gets Deleted')}</h3>
              <ul className={styles.list}>
                <li>{t('deletion.what_gets_deleted.item1', 'Your FOTH account and profile information')}</li>
                <li>{t('deletion.what_gets_deleted.item2', 'Meta / Instagram OAuth tokens, account IDs, and stored profile insights')}</li>
                <li>{t('deletion.what_gets_deleted.item3', 'Uploaded documents, verification media, and personal contact details')}</li>
                <li>{t('deletion.what_gets_deleted.item4', 'Historic collaboration metrics are anonymized where required for legal and tax accounting')}</li>
              </ul>
            </div>

            <div className={styles.deletionInfoCard}>
              <AlertCircle size={22} className={styles.deletionIcon} style={{ color: '#d97706' }} />
              <h3>{t('legal:privacy.sections.data_retention.title', 'Retention & Compliance')}</h3>
              <p className={styles.deletionNote}>
                {t('legal:privacy.sections.data_retention.content', 'Any retained transaction records are kept strictly to comply with tax and anti-fraud regulations.')}
              </p>
            </div>

            <div className={styles.deletionInfoCard}>
              <Clock size={22} className={styles.deletionIcon} style={{ color: '#059669' }} />
              <h3>{t('legal:privacy.sections.data_retention.title', 'Timeframe')}</h3>
              <p>{t('deletion.form.success_desc', { ref: '30 days', defaultValue: 'Deletion requests are processed within 30 days.' })}</p>
            </div>
          </section>

          {/* Disconnect Instagram */}
          <section className={styles.disconnectSection}>
            <h2 className={styles.deletionStepsTitle}>{t('legal:privacy.sections.instagram_api.user_control', 'Disconnecting Your Instagram Account')}</h2>
            <p className={styles.deletionStepsDesc}>
              {t('legal:privacy.sections.instagram_api.user_control', 'You can revoke FOTH access directly from your Instagram or Meta settings.')}
            </p>
            <div className={styles.disconnectNote}>
              <Mail size={14} />
              <a href="https://www.facebook.com/settings?tab=applications" target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
                Facebook → Settings → Apps &amp; Websites
              </a>
            </div>
          </section>

          {/* Form */}
          <section className={styles.deletionFormSection}>
            <h2 className={styles.deletionStepsTitle}>{t('deletion.title', 'Submit a Deletion Request')}</h2>
            <p className={styles.deletionStepsDesc}>
              {t('deletion.desc', 'Submit a deletion request below and our team will process it manually within 30 days.')}
            </p>

            {submitted ? (
              <div className={styles.successState}>
                <CheckCircle size={40} className={styles.successIcon} />
                <h3>{t('deletion.form.success_title', 'Request Received')}</h3>
                <p>
                  {t('deletion.form.success_desc', { ref: reference, defaultValue: `Your request has been logged under reference ${reference}.` })}
                </p>
                {reference && <p>Reference: <strong>{reference}</strong></p>}
              </div>
            ) : (
              <form className={styles.deletionForm} onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: 'absolute', left: '-10000px', opacity: 0 }}
                  aria-hidden="true"
                />
                {error && (
                  <div className={styles.formError}>
                    <AlertCircle size={15} /> {error}
                  </div>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('deletion.form.name_label', 'Full Name')} *</label>
                    <input
                      type="text"
                      name="name"
                      className={styles.formInput}
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t('deletion.form.name_placeholder', 'Your full name')}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('deletion.form.email_label', 'Email Address')} *</label>
                    <input
                      type="email"
                      name="email"
                      className={styles.formInput}
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('deletion.form.email_placeholder', 'your@email.com')}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('deletion.form.reason_label', 'Reason (optional)')}</label>
                  <textarea
                    name="reason"
                    className={styles.formTextarea}
                    value={form.reason}
                    onChange={handleChange}
                    placeholder={t('deletion.form.reason_placeholder', 'Help us improve: why are you requesting deletion?')}
                    rows={3}
                  />
                </div>

                <button
                  type="submit"
                  className={styles.deletionSubmitBtn}
                  disabled={loading}
                >
                  {loading ? t('deletion.form.submitting', 'Submitting…') : t('deletion.form.submit_btn', 'Submit Deletion Request')}
                </button>
              </form>
            )}
          </section>

          {/* Contact fallback */}
          <section className={styles.contactFallback}>
            <p>
              <Link to="/privacy" className={styles.inlineLink}>← {t('common.back_to_home', 'Back to Privacy Policy')}</Link>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
